/* eslint-disable no-loop-func */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useAuth, useLanguage } from 'hooks';
import { Typography } from '@material-ui/core';
import { map, set } from 'lodash';
import moment from 'moment';
import 'moment/locale/fr';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { useScheduleCalendar } from 'hooks/scheduleCalendar';
import { showModal } from 'state/actions';
import { COLOR, MODAL } from 'config/constants';
import {
  setCurrentView,
  setCurrentDate,
  setCurrentMonth,
  setEvents,
  setModalType,
  setAvailabilityToBeDeleted,
  setUnavailabilityToBeDeleted,
  setShootsToDisplay,
} from 'state/slices/scheduleCalender';
import WeekView from './WeekView';
import 'react-big-calendar/lib/css/react-big-calendar.css';

interface Props {
  schedule: any;
  selected_tech_id: number;
}

const useStyles = makeStyles({
  calendarContainer: {
    width: '100%',
    backgroundColor: COLOR.WHITE,
    padding: 5,
    borderRadius: 8,
    color: COLOR.BLACK,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  eventTitle: {
    fontSize: 12,
  },
  eventBody: {
    fontSize: 10,
    fontFamily: 'FordAntenna-Regular',
  },
  weeksColumn: {
    paddingTop: '8%',
    height: '100%',
    marginLeft: 20,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    backgroundColor: COLOR.WHITE,
  },
  weekRow: {
    height: 110,
    alignItems: 'center',
  },
  weekRowText: {
    fontSize: 11,
  },
});

function ScheduleCalendar({ schedule, selected_tech_id }: Props) {
  const intl = useIntl();
  const classes = useStyles();
  const language = useLanguage();
  moment.locale(language);
  const localizer = momentLocalizer(moment);
  const { user, token } = useAuth();
  const dispatch = useDispatch();
  const { isAdmin } = user;
  const { currentView, currentDate, currentMonth, events } = useScheduleCalendar(token);

  const [groupedShootings, setGroupedShootings] = useState<any>({});
  const [foundGroupedShooting, setFoundGroupedShooting] = useState(false);
  const [currentViewRange, setCurrentViewRange] = useState<any>([]);
  const [eventsWithProps, setEventsWithProps] = useState<any>([]);

  const messageFr = {
    week: 'La semaine',
    work_week: 'Semaine de travail',
    day: 'Jour',
    month: 'Mois',
    previous: 'Antérieur',
    next: 'Prochain',
    today: "Aujourd'hui",
    agenda: 'Ordre du jour',
  };
  const messages = language === 'fr' ? messageFr : null;
  const fmtAvailable = intl.formatMessage({ id: 'prodScheduler.available' });
  const fmtNewAvailability = intl.formatMessage({ id: 'prodScheduler.newAvailability' });
  const fmtReason = intl.formatMessage({ id: 'prodScheduler.reason' });
  const fmtUnassignedShoot = intl.formatMessage({ id: 'prodScheduler.unassignedShoot' });
  const fmtNewUnavailability = intl.formatMessage({ id: 'prodScheduler.newUnavailability' });
  const fmtSeaonalShoots = intl.formatMessage({ id: 'prodScheduler.seasonalShoots' });
  const fmtStandardShoots = intl.formatMessage({ id: 'prodScheduler.standardShoots' });
  const fmtShoots = intl.formatMessage({ id: 'prodScheduler.shoots' });
  const groupWeeksOfMonth = () => {
    const weeks = [];
    const startOfMonth =
      currentViewRange && currentViewRange.start
        ? moment(currentViewRange.start)
        : moment(currentDate).startOf('month');
    const endOfMonth =
      currentViewRange && currentViewRange.end
        ? moment(currentViewRange.end)
        : moment(currentDate).endOf('month');

    const startOfNextMonth = moment(currentDate).add(1, 'month').startOf('month');
    let currentWeek = [];
    const currentDay = startOfMonth.clone().startOf('week');
    while (currentDay.isSameOrBefore(endOfMonth)) {
      currentWeek.push(currentDay.toDate());

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      currentDay.add(1, 'day');
    }
    // Push the last incomplete week if exists
    if (currentWeek.length > 0) {
      // Check if the last week is less than 7 days
      const remainingDays = 7 - currentWeek.length;
      // Add remaining days from the next month
      for (let i = 0; i < remainingDays; i++) {
        currentWeek.push(startOfNextMonth.clone().add(i, 'days').toDate());
      }
      weeks.push(currentWeek);
    }
    return weeks;
  };

  const groupShootingsByWeeks = (shootings, weeks) => {
    const shootingsByWeeks = weeks.map(() => {
      return [];
    });
    shootings.forEach((shoot) => {
      const shootingDate = moment(shoot.scheduleddate);
      // Find the week index that contains the shooting date
      const weekIndex = weeks.findIndex((week) => {
        return shootingDate.isBetween(moment(week[0]), moment(week[6]), 'day', '[]');
      });
      // If shooting belongs to any week, add it to the corresponding week's shootings
      if (weekIndex !== -1) {
        shootingsByWeeks[weekIndex].push({
          shoot,
          start: moment(weeks[weekIndex][0]),
          end: moment(weeks[weekIndex][6]),
        });
      }
    });
    return shootingsByWeeks;
  };

  const groupShootingsByWeek = (shootings) => {
    let groupedShootings = {};
    groupedShootings = groupShootingsByWeeks(shootings, groupWeeksOfMonth());
    return groupedShootings;
  };

  useEffect(() => {
    let shootings = schedule?.shootings || [];
    if (selected_tech_id) {
      shootings = shootings.filter((shooting) => {
        return shooting.tech_id === selected_tech_id;
      });
    }
    const groupedShootings = groupShootingsByWeek(shootings);
    setGroupedShootings(groupedShootings);
    let foundGroupedShooting = false;
    Object.keys(groupedShootings).map((weekNumber) => {
      if (groupedShootings[weekNumber].length > 0) {
        foundGroupedShooting = true;
      }
      return null;
    });
    setFoundGroupedShooting(foundGroupedShooting);
  }, [schedule, language, selected_tech_id]);
  const createEventsForMonthView = (data: any) => {
    if (!data) {
      return [];
    }
    const { availabilities = [], unAvailabilities = [], shootings = [] } = data;
    const available_events = availabilities.map((value) => {
      return {
        start: moment(value.date).add(23, 'hours').toDate(),
        end: moment(value.date).add(23, 'hours').toDate(),
        allDay: false,
        type: 'availabilities',
        availability: value,
      };
    });

    const unavailable_events = unAvailabilities.map((unavail) => {
      return {
        id: unavail.id,
        start: moment(unavail.start).toDate(),
        end: moment(unavail.end).toDate(),
        type: 'unavailability',
        tech: unavail.user,
        summary: unavail.summary,
      };
    });

    const shooting_events = {};

    shootings.forEach((shooting) => {
      const todayDate = moment(shooting.scheduleddate).format('MM/DD/YYYY');
      if (!shooting_events[todayDate]) {
        shooting_events[todayDate] = [];
      }
      shooting_events[todayDate].push(shooting);
    });

    const shooting_events_mapped = map(shooting_events, (shootings: any, date) => {
      return {
        start: moment(date).toDate(),
        end: moment(date).toDate(),
        type: 'shooting',
        techs: shootings.length,
      };
    });

    return [].concat(available_events, unavailable_events, shooting_events_mapped);
  };

  const createEventsForWeekView = (data: any) => {
    if (!data) {
      return [];
    }
    const { availabilities = [], unAvailabilities = [], shootings = [] } = data;

    return [].concat(
      availabilities.map((unavail) => {
        return {
          start: moment(unavail.date).add(12, 'hours').toDate(),
          end: moment(unavail.date).add(12, 'hours').toDate(),
          allDay: true,
          type: 'availabilities',
          tech: unavail.user,
        };
      }),
      unAvailabilities.map((unavail) => {
        return {
          id: unavail.id,
          start: moment(unavail.start).toDate(),
          end: moment(unavail.end).toDate(),
          type: 'unavailability',
          tech: unavail.user,
          summary: unavail.summary,
        };
      }),
      shootings.map((shoot) => {
        return {
          start: moment(shoot.scheduleddate).toDate(),
          end: moment(shoot.scheduleddate).add(shoot.estimated_duration, 'seconds').toDate(),
          type: 'shooting',
          tech: shoot.tech,
          address: `${shoot.listing.app ? `${shoot.listing.app}, ` : ''} ${
            shoot.listing.address
          }, ${shoot.listing.city}, ${shoot.listing.postalcode}`,
          shoot,
        };
      }),
    );
  };

  const getEventsWithProps = () => {
    let newEvents;
    if (selected_tech_id === null) {
      newEvents = events;
    } else {
      const filteredSchedule = {
        ...schedule,
        availabilities: schedule.availabilities.filter((a) => {
          return a.user_id === selected_tech_id;
        }),
        shootings: schedule.shootings.filter((s) => {
          return s.tech_id === selected_tech_id;
        }),
        unAvailabilities: schedule.unAvailabilities.filter((u) => {
          return u.user_id === selected_tech_id;
        }),
      };
      if (currentView === 'month') {
        newEvents = createEventsForMonthView(filteredSchedule);
      } else {
        newEvents = createEventsForWeekView(filteredSchedule);
      }
    }
    newEvents = newEvents.map((eve) => {
      return { ...eve, ...{ isAdmin } };
    });
    return newEvents;
  };

  useEffect(() => {
    let newEvents;
    if (currentView === 'month') {
      newEvents = createEventsForMonthView(schedule);
    } else {
      newEvents = createEventsForWeekView(schedule);
    }
    dispatch(setEvents(newEvents));
  }, [currentView, schedule]);

  useEffect(() => {
    setEventsWithProps(getEventsWithProps());
  }, [currentView, schedule, selected_tech_id, events]);

  const handleViewChange = (view: string) => {
    dispatch(setCurrentView(view));
  };

  const onCreate = (slots: any, endTime?: any) => {
    if (slots.length < 1) {
      return;
    }
    let start = moment(slots[0]);
    let end = moment(slots[slots.length - 1]);
    if (endTime) {
      end = moment(endTime);
    }
    if (currentView === 'month') {
      start = moment(start);
      end = end.endOf('day');
      const newEvent = {
        start: start.toDate(),
        end: end.toDate(),
        allDay: true,
        type: 'new-availability',
      };
      const newEvents = events.concat(newEvent);
      dispatch(setEvents(newEvents));
      dispatch(showModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType('new-availability'));
    } else {
      start = start.second(0);
      end = end.second(0);
      const newEvent = {
        start: start.toDate(),
        end: end.toDate(),
        type: 'new-unavailability',
      };
      const newEvents = events.concat(newEvent);
      dispatch(setEvents(newEvents));
      dispatch(showModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType('new-unavailability'));
    }
  };

  const onDateChange = (slotInfo: any) => {
    const { start, end, action, slots, ...props } = slotInfo;
    console.log('action', action);
    if (action === 'select') {
      // create a new unavailability
      if (currentView === 'month') {
        onCreate(slots);
      } else {
        onCreate(slots, end);
      }
    } else if (action === 'click') {
      handleViewChange('day');
      if (moment(start).month() !== currentMonth) {
        dispatch(setCurrentMonth(moment(start).month()));
      }
      dispatch(setCurrentDate(moment(start).toDate()));
    }
  };

  const onAvailabilityClick = (event: any) => {
    dispatch(showModal(MODAL.SCHEDULER_MODAL));
    dispatch(setModalType('remove-availability'));
    dispatch(setAvailabilityToBeDeleted(event));
  };

  const onUnavailabilityClick = (event: any) => {
    dispatch(setUnavailabilityToBeDeleted(event));
    dispatch(showModal(MODAL.SCHEDULER_MODAL));
    dispatch(setModalType('remove-unavailability'));
  };

  const onShootingClick = (event: any) => {
    let shoots;
    if (currentView !== 'month') {
      // single shoot clicked
      shoots = [event.shoot];
    } else {
      shoots = schedule.shootings.filter((shoot) => {
        return moment(shoot.scheduleddate).isSame(moment(event.start), 'date');
      });
    }
    dispatch(setShootsToDisplay(shoots));
    dispatch(showModal(MODAL.SCHEDULER_MODAL));
    dispatch(setModalType('display-shootings'));
  };

  const handleEventClick = (event: any) => {
    if (event.type === 'availabilities') {
      onAvailabilityClick(event);
    } else if (event.type === 'unavailability') {
      onUnavailabilityClick(event);
    } else if (event.type === 'shooting') {
      onShootingClick(event);
    }
  };

  const onNavigate = (date: Date) => {
    if (moment(date).month() !== currentMonth) {
      dispatch(setCurrentMonth(moment(date).month()));
    }
    dispatch(setCurrentDate(date));
  };

  const EventUIBar = (props) => {
    const { title, body } = props;
    return (
      <span className={classes.eventTitle}>
        <strong className={classes.eventBody}>{title}</strong>
        {body}
      </span>
    );
  };

  const monthEventView = ({ event }: any) => {
    switch (event.type) {
      case 'availabilities':
        return (
          <EventUIBar
            title={
              event.isAdmin
                ? `${event.availability?.user.firstname} ${event.availability?.user.lastname}`
                : null
            }
            body={event.isAdmin ? '' : ` ${fmtAvailable}`}
          />
        );
      case 'unavailability':
        return (
          <EventUIBar
            title={`${moment(event.start).format('HH:mm')}`}
            body={event.isAdmin && event.tech && ` ${event.tech.firstname} ${event.tech.lastname}`}
          />
        );
      case 'shooting':
        return <EventUIBar title={event.techs} body={` ${fmtShoots}`} />;
      case 'new-availability':
        return <EventUIBar body={fmtNewAvailability} />;
      default:
        return null;
    }
  };
  const weekEventView = ({ event }: any) => {
    switch (event.type) {
      case 'availabilities':
        return (
          <div>
            <Typography className={classes.eventTitle}>
              {event.tech && `${event.tech.firstname} ${event.tech.lastname}`}
            </Typography>
          </div>
        );
      case 'unavailability':
        return (
          <div>
            <Typography className={classes.eventTitle}>
              {event.tech && `${event.tech.firstname} ${event.tech.lastname}`}
            </Typography>
          </div>
        );
      case 'shooting':
        return (
          <div>
            <Typography style={{ fontSize: '10px' }}>
              {event.tech != null
                ? `${event.tech.firstname} ${event.tech.lastname}`
                : fmtUnassignedShoot}
            </Typography>
            <Typography style={{ fontSize: '10px' }}>{event.address}</Typography>
          </div>
        );
      default:
        return null;
    }
  };
  const dayEventView = ({ event }: any) => {
    switch (event.type) {
      case 'availabilities':
        return (
          <div>
            <Typography className={classes.eventTitle}>
              {event.tech && `${event.tech.firstname} ${event.tech.lastname}`}
            </Typography>
          </div>
        );
      case 'unavailability':
        return (
          <div>
            <Typography className={classes.eventTitle}>
              {event.tech && `${event.tech.firstname} ${event.tech.lastname}`}
            </Typography>
            {event.summary != null && event.summary !== '' ? (
              <Typography className={classes.eventTitle}>
                {`${fmtReason} - ${event.summary}`}
              </Typography>
            ) : null}
          </div>
        );
      case 'shooting':
        return (
          <div>
            <Typography className={classes.eventTitle}>
              {(event.tech && `${event.tech.firstname} ${event.tech.lastname}`) ||
                fmtUnassignedShoot}
            </Typography>
            <Typography className={classes.eventTitle}>{event.address}</Typography>
          </div>
        );
      case 'new-unavailability':
        return (
          <div>
            <Typography className={classes.eventTitle}>{fmtNewUnavailability}</Typography>
          </div>
        );
      default:
        return null;
    }
  };

  const getCurrentWeekShootingsReport = () => {
    return (
      <div
        className={classes.weeksColumn}
        style={{
          display: foundGroupedShooting ? 'flex' : 'none',
          width: foundGroupedShooting ? '20%' : 0,
        }}
      >
        {Object.keys(groupedShootings).map((weekNumber) => {
          let normal_shoots = 0;
          let seasonal_shoots = 0;
          groupedShootings[weekNumber].forEach((group) => {
            const { shoot } = group;
            if (shoot && shoot.service_offers.length) {
              const { service_offers } = shoot;
              const foundSeasonalServiceOffer = service_offers.findIndex((so) => {
                return so.serviceOffer.display_name.includes('Seasonal');
              });
              if (foundSeasonalServiceOffer !== -1) {
                seasonal_shoots += 1;
              } else {
                normal_shoots += 1;
              }
            } else {
              normal_shoots += 1;
            }
          });
          const start = groupedShootings[weekNumber][0]?.start || '';
          const end = groupedShootings[weekNumber][0]?.end || '';
          return (
            <div key={weekNumber} className={classes.weekRow}>
              {start && end ? (
                <Typography className={classes.weekRowText}>
                  {`${moment(start).format('MM/DD')} - ${moment(end).format('MM/DD')}`}
                </Typography>
              ) : null}
              <Typography className={classes.weekRowText}>
                {seasonal_shoots > 0 ? `${fmtSeaonalShoots}: ${seasonal_shoots}` : null}
              </Typography>
              <Typography className={classes.weekRowText}>
                {normal_shoots > 0 ? `${fmtStandardShoots}: ${normal_shoots}` : null}
              </Typography>
            </div>
          );
        })}
      </div>
    );
  };

  const eventWithStyles = (event, currentView) => {
    switch (event.type) {
      case 'availabilities':
        return { style: { backgroundColor: 'green' } };
      case 'unavailability':
        return { style: { backgroundColor: 'red', maxWidth: '300px' } };
      case 'shooting':
        return { style: { backgroundColor: 'blue', maxWidth: '300px', border: '1px solid black' } };
      default:
        return null;
    }
  };
  return (
    <div className={classes.calendarContainer}>
      <Calendar
        localizer={localizer}
        messages={messages}
        events={eventsWithProps}
        startAccessor='start'
        endAccessor='end'
        style={{
          width: foundGroupedShooting ? '88%' : '100%',
          height: 700,
          color: COLOR.CHARCOAL,
          fontFamily: 'FordAntenna-Regular',
        }}
        popup
        selectable
        eventPropGetter={(props) => {
          return eventWithStyles({ ...props }, currentView);
        }}
        view={currentView}
        date={currentDate}
        min={moment().set('hour', 6).set('minute', 0).toDate()}
        max={moment().set('hour', 21).set('minute', 0).toDate()}
        views={{
          month: true,
          week: WeekView,
          day: true,
        }}
        components={{
          month: {
            event: monthEventView,
          },
          week: {
            event: weekEventView,
          },
          day: {
            event: dayEventView,
          },
        }}
        onNavigate={onNavigate}
        onSelectSlot={(slotInfo) => {
          return onDateChange(slotInfo);
        }}
        onView={handleViewChange}
        onSelectEvent={handleEventClick}
        onRangeChange={(range) => {
          setCurrentViewRange(range);
        }}
      />
      {currentView === 'month' ? getCurrentWeekShootingsReport() : null}
    </div>
  );
}

export default ScheduleCalendar;
